import React, { useEffect, useState } from "react";
import {
    Button,
    Col,
    Icon,
    Input,
    Modal,
    Radio,
    Row,
    Select,
    Tooltip
} from "antd";
import style from "./index.module.scss";
import {
    createOrUpdateSignIn,
    SignInDetail,
    SignStatus,
    SignType
} from "service/signIn";
import useRequestFn from "utils/useRequestFn";
import ResultMessage from "components/CourseComponents/ResultMessage";
import { errorMessage, toNumber } from "service/service";

interface SettingModalProps {
    weekTimeId: number;
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    onStart?: (signInId: number) => void;
    defaultData: SignInDetail | null;
    mode?: "projection" | "normal"; // 投屏还是非投屏
}

export default function SettingModal({
    visible,
    onClose,
    onStart,
    weekTimeId,
    onSuccess,
    defaultData,
    mode
}: SettingModalProps) {
    const defaultType = SignType.Number;
    const defaultRefreshTime = 10;
    const defaultValidityTime = -1;
    const defaultStatus = 0;

    const { Option } = Select;
    const [title, setTitle] = useState("");
    const [type, setType] = useState<SignType>(defaultType); // 签到类型 1:数字码2：普通签到
    const [refreshTime, setRefreshTime] = useState(defaultRefreshTime);
    const [validityTime, setValidityTime] =
        useState<number>(defaultValidityTime);

    const [status, setStatus] = useState(defaultStatus);
    const [addNewSignIn, addState] = useRequestFn(params =>
        createOrUpdateSignIn(params)
    );

    const id = defaultData?.id || -1;
    const isDigital = type === SignType.Number; // 是否数字码签到
    const isEdit = !!defaultData; // 是否修改

    const firstSpan = 8;
    const secondSpan = 14;

    // 编辑时初始化数据
    useEffect(() => {
        if (!defaultData || !visible) return;
        setTitle(defaultData.title);
        setType(defaultData.type);
        setRefreshTime(defaultData.refreshTime || defaultRefreshTime);
        setValidityTime(defaultData.validityTime || defaultValidityTime);
        setStatus(defaultData.status);
    }, [defaultData, visible]);

    // 关闭表单重置数据
    function onReset() {
        setTitle("");
        setType(defaultType);
        setRefreshTime(defaultRefreshTime);
        setValidityTime(defaultValidityTime);
        setStatus(defaultStatus);
    }

    // 点击保存按钮
    async function saveData() {
        const params = {
            title: title.trim(),
            weekTimeId,
            type,
            refreshTime,
            validityTime: validityTime === -1 ? "" : validityTime
        };

        // 修改需要带的参数
        const editSignInParams = { id, status };

        if (isEdit) Object.assign(params, editSignInParams); // 修改

        const res = await addNewSignIn(params);
        const msg = !isEdit ? "创建成功！" : "修改成功！";
        ResultMessage.success(msg);
        onClose();
        return res;
    }

    // 保存/确认
    async function onConfirm() {
        try {
            await saveData();
            onSuccess();
        } catch (e) {
            const msg = !defaultData ? "创建失败！" : "修改失败！";
            ResultMessage.error(errorMessage(e, msg));
        }
    }

    // 立即开始(普通签到)
    async function onSaveAndStart() {
        try {
            const res = await saveData();
            onStart?.(toNumber(res.message));
        } catch (e) {
            const msg = !defaultData ? "创建失败！" : "修改失败！";
            ResultMessage.error(errorMessage(e, msg));
        }
    }

    function renderFooter() {
        // 未开始或新建
        if (defaultData?.status === SignStatus.Idle || !isEdit) {
            return (
                <div>
                    <Button onClick={onConfirm}>保存</Button>
                    <Button
                        type="primary"
                        onClick={onSaveAndStart}
                        loading={addState.loading}>
                        立即开始
                    </Button>
                </div>
            );
        }
        return (
            <div>
                <Button onClick={onClose}>取消</Button>
                <Button
                    type="primary"
                    onClick={onConfirm}
                    loading={addState.loading}>
                    确认
                </Button>
            </div>
        );
    }

    function getContainer(): any {
        return document.querySelector(".PopupContainer");
    }

    return (
        <Modal
            className={style.root}
            title={isEdit ? "签到设置" : "新建签到"}
            visible={visible}
            width={610}
            onCancel={onClose}
            maskClosable={false}
            afterClose={onReset}
            getContainer={false}
            footer={renderFooter()}>
            <Row>
                <Col span={firstSpan} className={style.firstSpan}>
                    名称：
                </Col>
                <Col span={secondSpan} className={style.secondSpan}>
                    <Input
                        className={style.input}
                        placeholder="输入签到活动名称"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        maxLength={500}
                    />
                </Col>
            </Row>

            <Row>
                <Col span={firstSpan} className={style.firstSpan}>
                    签到方式：
                </Col>
                <Col span={secondSpan} className={style.secondSpan}>
                    <Radio.Group
                        value={type}
                        onChange={e => setType(e.target.value)}
                        disabled={
                            isEdit && defaultData?.status !== SignStatus.Idle
                        }>
                        <Radio value={SignType.Number}>数字码签到</Radio>
                        <Radio value={SignType.Normal}>普通签到</Radio>
                    </Radio.Group>
                </Col>
            </Row>

            {isDigital && (
                <Row>
                    <Col span={firstSpan} className={style.firstSpan}>
                        数字码有效时长：
                    </Col>
                    <Col span={secondSpan} className={style.secondSpan}>
                        <Select
                            className={style.select}
                            dropdownClassName={style.dropdown}
                            value={refreshTime}
                            onChange={(value: number) => setRefreshTime(value)}
                            getPopupContainer={getContainer}>
                            <Option className={style.option} value={6}>
                                6
                            </Option>
                            <Option className={style.option} value={10}>
                                10
                            </Option>
                            <Option className={style.option} value={15}>
                                15
                            </Option>
                            <Option className={style.option} value={20}>
                                20
                            </Option>
                        </Select>
                        <span className={style.unit}>秒</span>
                    </Col>
                </Row>
            )}

            <Row>
                <Col span={firstSpan} className={style.firstSpan}>
                    活动时长：
                </Col>
                <Col span={secondSpan} className={style.secondSpan}>
                    <Select
                        className={style.select}
                        dropdownClassName={style.dropdown}
                        value={validityTime}
                        onChange={(value: number) => setValidityTime(value)}
                        getPopupContainer={getContainer}>
                        <Option className={style.option} value={-1}>
                            不限
                        </Option>
                        <Option className={style.option} value={3}>
                            3
                        </Option>
                        <Option className={style.option} value={5}>
                            5
                        </Option>
                        <Option className={style.option} value={10}>
                            10
                        </Option>
                        <Option className={style.option} value={15}>
                            15
                        </Option>
                    </Select>
                    <span className={style.unit}>分钟</span>
                    <Tooltip
                        title="系统根据设置的签到活动时长自动结束签到，活动时长为“不限”时，表示需要教师手动结束签到"
                        getPopupContainer={getContainer}>
                        <Icon
                            type="exclamation-circle"
                            className={style.notice}
                        />
                    </Tooltip>
                </Col>
            </Row>
        </Modal>
    );
}
